// useVesselNamingText.ts
import { BufferPoolItem, SurfaceContext, useBufferPool, VRContext } from '@features/analysis/rendering/Panels/Renderers/vtkManagers';
import { getBranchType, getWorldToIndex } from './Surface3DUtilities';
import { usePanelContainer } from '@features/analysis/rendering/Panels/usePanelContainer';
import { getLabelmapSegmentationCachedStats, getLabelVolumeFromSeriesId, getVolumeFromSeriesId } from '@features/analysis/rendering/Panels/Renderers/CornerstoneManager';
import { configStore } from 'config/configStore';
import { BranchInfo, findNearBranch } from '../../utils/FunctionUtilites';
import { Vector3 } from '@kitware/vtk.js/types';
import { useBrowserPage } from '@features/browser/useBrowserPage';
import { MarkerLinesMoveRender } from '@features/analysis/rendering/Panels/Renderers/vtkManagers';
import { scheduleRender } from '@features/analysis/rendering/Panels/renderState';
import { getSegmentationBits } from './VRUtilities';

type useBranchSelectionParams = {
  context: React.MutableRefObject<SurfaceContext | VRContext | null>,
  lowQuality: boolean;
};

function clampIndex(value: number, min: number, max: number) {
  if (max < min) return min;
  return Math.min(max, Math.max(min, value));
}

function findNearestBranchPathIndex(
  info: BranchInfo,
  branchId: number,
  totalIndex: number,
) {
  const directIndex = info.findSelectBranchIndex(branchId, totalIndex);
  if (directIndex >= 0) return directIndex;

  const branchIndex = info.getBranchIndex()?.[branchId];
  if (!branchIndex || branchIndex.length === 0) return -1;

  let nearestIndex = 0;
  let nearestDistance = Number.MAX_VALUE;
  for (let i = 0; i < branchIndex.length; i++) {
    const distance = Math.abs(branchIndex[i] - totalIndex);
    if (distance < nearestDistance) {
      nearestDistance = distance;
      nearestIndex = i;
    }
  }
  return nearestIndex;
}

export function useBranchSelectionHandler({
  context,
  lowQuality,
}: useBranchSelectionParams) {

  const { updateCPRState } = usePanelContainer();
  const { selectedSeriesId } = useBrowserPage();
  const { bufferPool } = useBufferPool();
  const pool = selectedSeriesId ? bufferPool[selectedSeriesId] : undefined;

  function getSelectedBranchIdAndNodeIndex(
    pickedPoint: [number, number, number],
  ) {
    if (!pool) return;
    if (!pickedPoint || !selectedSeriesId) return [-1, -1];
    const { renderingQuality } = configStore.get().generalSetting;
    const labelVolume = getVolumeFromSeriesId(
      selectedSeriesId,
      renderingQuality !== 'high',
    );
    if (!labelVolume) return [-1, -1];
    const startNode = pool['startNode_list'].data as Int32Array;
    const info = getSelectedBranchId(selectedSeriesId,pool,pickedPoint);
    const id = info[0];
    if (id < 0) return [-1, -1];
    const Index_total = info[1];
    const branch_start_node = startNode[id];
    const index = Index_total - branch_start_node;
    return [id, index];
  }

  function getSelectedBranchId(
    seriesId: string,
    pool: BufferPoolItem,
    pickedPoint: [number, number, number],
  ) {
    if (!pickedPoint) return [-1, -1];
    const { renderingQuality } = configStore.get().generalSetting;
    const half = renderingQuality!=='high';
    const labelVolume = getVolumeFromSeriesId(
      seriesId,
      half,
    );
    if (!labelVolume) return [-1, -1];
    const { imageData, origin, spacing, dimensions } = labelVolume;
    if(!imageData) return [-1, -1];


    if(context.current?.viewportId === 'VR'){
      const segVolume = getLabelVolumeFromSeriesId(seriesId,half);
      if (!segVolume) return  [-1, -1];
      const labelData =
        segVolume.voxelManager?.getCompleteScalarDataArray?.() as Uint8Array;
      const cachedStats = getLabelmapSegmentationCachedStats(seriesId,half);
      const coronary = getSegmentationBits(cachedStats, ['coronary','calcium']);
      const [nx, ny, nz] = dimensions;
      const [x, y, z] = imageData.worldToIndexVec3(pickedPoint).map(Math.round);
      if (x < 0 || x >= nx || y < 0 || y >= ny || z < 0 || z >= nz)  [-1, -1];
      const offset = z * ny * nx + y * nx + x;
      if(!coronary?.has(labelData[offset])){
        return [-1, -1];
      }
    }

    const fcx = pool['fcx'].data as Int32Array;
    const fcy = pool['fcy'].data as Int32Array;
    const fcz = pool['fcz'].data as Int32Array;
    const branchid = pool['branchid'].data as Int32Array;
    if (!fcx || !fcy || !fcz || !branchid) return [-1, -1];
    const result: Vector3[] = [];
    const branchIds: number[] = [...branchid];
    for (let i = 0; i < fcx.length; i++) {
      result.push([fcx[i], fcy[i], fcz[i]]);
    }

    const position = getWorldToIndex(
      pickedPoint,
      spacing,
      origin,
      renderingQuality !== 'high'
    );
    const info = findNearBranch(position, result, branchIds);
    return info;
  }

  function handleBranchSelection(
    pickedPoint: [number, number, number]
  ) {
    if (!pool || !context.current) return;
    if (!pickedPoint || !selectedSeriesId) return;

    const [branchId, nodeIndex] = getSelectedBranchId(
      selectedSeriesId,
      pool,
      pickedPoint,
    );

    if (branchId < 0 || nodeIndex < 0) return;

    const info = pool.branchGraphDataset.data as BranchInfo;
    if(!info) return;
    const mainIndex = 0;

    const selindex = findNearestBranchPathIndex(info, branchId, nodeIndex);
    const maxLength =
      info.getLengthArray(branchId)?.length ??
      info.getBranchIndex()?.[branchId]?.length ??
      0;
    if (selindex < 0 || maxLength <= 0) return;

    const frontExpand = configStore.get().coronary_front_expand;
    const validStart = frontExpand;
    const validEnd = maxLength - 1 + frontExpand;
    const halfWindow = 50;

    let mid = clampIndex(selindex + frontExpand, validStart, validEnd);
    let front = clampIndex(mid - halfWindow, validStart, validEnd);
    let back = clampIndex(mid + halfWindow, validStart, validEnd);
    if (mid < front) mid = front;
    if (back < mid) back = mid;

    const pick = [
      { index: front },
      { index: mid },
      { index: back },
    ];

    updateCPRState({
      mainIndex: { index: mainIndex, branchId },
      indexedStates: {
        [branchId]: pick,
      },
    });

    requestAnimationFrame(() => {
      MarkerLinesMoveRender(
        0,
        branchId,
        [0, 1, 2],
        [front, mid, back],
        pool,
      );

      const indicatorArgs = {
        type: 'externalFocusIndicator',
        branchId,
        index: mid,
      };
      scheduleRender(`CPR.${mainIndex}.stretched`, indicatorArgs);
      scheduleRender(`CPR.${mainIndex}.straightened`, indicatorArgs);
    });
  }
  return {
    handleBranchSelection,
    getSelectedBranchIdAndNodeIndex,
    getSelectedBranchId,
  };
}
